import { Workbook, Row } from 'exceljs';
import { saveAs } from 'file-saver-es';
import { LeveyJenningsReportRowDTO } from '../../types/types.qualityControl';
import { CapturedImage } from './qc-chart-capture';

export interface WestgardExcelHeader {
  fechaDesde: string; // ISO o texto
  fechaHasta: string;
  sedeNombre?: string;
  analizadorNombre?: string;
  loteCodigo?: string;
  generadoEn: string;
}

const ESTADO_TEXTO: Record<number, string> = { 1: 'PENDIENTE', 2: 'VALIDADO', 3: 'RECHAZADO', 4: 'ALERTA' };

const HEADER_FILL = '03A9F4';

const fmtFecha = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const fmtDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const reglasResumen = (r: LeveyJenningsReportRowDTO): string =>
  r.ResumenViolaciones && r.ResumenViolaciones.length > 0 ? r.ResumenViolaciones.map((v) => `${v.Regla} x${v.Conteo}`).join(', ') : '-';

// Nombres de hoja válidos para Excel (<=31, sin caracteres prohibidos, únicos)
const safeSheetName = (base: string, used: Set<string>): string => {
  let name = base.replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 28) || 'Control';
  let candidate = name;
  let i = 2;
  while (used.has(candidate.toLowerCase())) {
    candidate = `${name.slice(0, 25)} ${i}`;
    i++;
  }
  used.add(candidate.toLowerCase());
  return candidate;
};

const styleHeaderRow = (row: Row) => {
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${HEADER_FILL}` } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FFBDBDBD' } } };
  });
};

export const generateWestgardExcel = async (
  rows: LeveyJenningsReportRowDTO[],
  images: Map<number, CapturedImage>,
  header: WestgardExcelHeader,
  fileName?: string
): Promise<void> => {
  const wb = new Workbook();
  wb.creator = 'SynerLab';
  wb.created = new Date();

  // ─── Hoja Resumen ───
  const resumen = wb.addWorksheet('Resumen', { views: [{ state: 'frozen', ySplit: 6 }] });
  resumen.mergeCells('A1:K1');
  resumen.getCell('A1').value = 'Informe Levey-Jennings / Westgard';
  resumen.getCell('A1').font = { bold: true, size: 15, color: { argb: `FF${HEADER_FILL}` } };
  resumen.getCell('A3').value = 'Período:';
  resumen.getCell('B3').value = `${fmtDate(header.fechaDesde)} - ${fmtDate(header.fechaHasta)}`;
  resumen.getCell('A4').value = 'Sede / Analizador:';
  resumen.getCell('B4').value = `${header.sedeNombre || 'Todas'}${header.analizadorNombre ? ` / ${header.analizadorNombre}` : ''}`;
  resumen.getCell('D3').value = 'Lote:';
  resumen.getCell('E3').value = header.loteCodigo || 'Todos';
  resumen.getCell('D4').value = 'Generado:';
  resumen.getCell('E4').value = fmtFecha(header.generadoEn);
  ['A3', 'A4', 'D3', 'D4'].forEach((c) => (resumen.getCell(c).font = { bold: true }));

  const headerRowIdx = 6;
  const resCols = ['Analito', 'Nivel', 'Lote', 'Analizador', 'N', 'Validadas', 'Con violaciones', 'Rechazos', 'Reglas violadas', 'Sigma', 'Clasificación'];
  resumen.getRow(headerRowIdx).values = resCols;
  styleHeaderRow(resumen.getRow(headerRowIdx));
  resumen.columns = [
    { width: 28 }, { width: 12 }, { width: 16 }, { width: 20 }, { width: 8 }, { width: 11 }, { width: 15 }, { width: 11 }, { width: 30 }, { width: 9 }, { width: 16 },
  ];

  rows.forEach((r) => {
    const row = resumen.addRow([
      r.AnalitoNombre,
      r.MaterialNivel || '',
      r.LoteCodigo || '',
      r.AnalizadorNombre || '',
      r.TotalMediciones,
      r.MedicionesValidadas,
      r.MedicionesConViolaciones,
      r.TotalRechazos,
      reglasResumen(r),
      r.SigmaActual == null ? '-' : Number(r.SigmaActual.toFixed(2)),
      r.ClasificacionSigma ? String(r.ClasificacionSigma).replace('_', ' ') : 'Manual',
    ]);
    if (r.MedicionesConViolaciones > 0) row.getCell(7).font = { color: { argb: 'FFF44336' }, bold: true };
    if (r.TotalRechazos > 0) row.getCell(8).font = { color: { argb: 'FFF44336' }, bold: true };
  });

  // ─── Una hoja por control con mediciones (gráfica + detalle) ───
  const usedNames = new Set<string>(['resumen']);
  const conMediciones = rows.filter((r) => (r.Mediciones || []).length > 0);

  for (const r of conMediciones) {
    const esCuant = r.TipoResultado !== 'CUALITATIVO';
    const sheetName = safeSheetName(`${r.AnalitoNombre} ${r.MaterialNivel || ''}`.trim(), usedNames);
    const ws = wb.addWorksheet(sheetName);

    ws.mergeCells('A1:G1');
    ws.getCell('A1').value = `${r.AnalitoNombre} - ${r.MaterialNivel || ''} (Lote ${r.LoteCodigo})`;
    ws.getCell('A1').font = { bold: true, size: 13 };

    ws.mergeCells('A2:G2');
    ws.getCell('A2').value = esCuant
      ? `Media: ${r.Media?.toFixed(2)}   SD: ${r.DesviacionEstandar?.toFixed(2)}   N: ${r.TotalMediciones}   Validadas: ${r.MedicionesValidadas}   Con violaciones: ${r.MedicionesConViolaciones}   Rechazos: ${r.TotalRechazos}`
      : `N: ${r.TotalMediciones}   Aprobadas: ${r.MedicionesAprobadas}   No aprobadas: ${r.MedicionesNoAprobadas}`;
    ws.getCell('A2').font = { color: { argb: 'FF666666' } };

    // Imagen de la gráfica
    let tableStartRow = 4;
    const img = images.get(r.ConfiguracionId);
    if (img && img.dataUrl) {
      const base64 = img.dataUrl.includes(',') ? img.dataUrl.split(',')[1] : img.dataUrl;
      const imgId = wb.addImage({ base64, extension: img.format === 'PNG' ? 'png' : 'jpeg' });
      const maxW = 680;
      const ratio = img.height > 0 ? img.height / img.width : 0.55;
      const w = Math.min(maxW, img.width);
      const h = w * ratio;
      ws.addImage(imgId, { tl: { col: 0, row: 3 }, ext: { width: w, height: h } });
      tableStartRow = 4 + Math.ceil(h / 18) + 1;
    }

    // Tabla de mediciones
    const cols = esCuant
      ? ['Fecha / Hora', 'Valor', 'Z-Score', 'Estado', 'Reglas violadas', 'Usuario', 'Nota']
      : ['Fecha / Hora', 'Resultado', 'Aprobado', 'Estado', 'Usuario', 'Nota'];
    const hRow = ws.getRow(tableStartRow);
    hRow.values = cols;
    styleHeaderRow(hRow);

    ws.columns = esCuant
      ? [{ width: 20 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 22 }, { width: 18 }, { width: 40 }]
      : [{ width: 20 }, { width: 18 }, { width: 12 }, { width: 14 }, { width: 18 }, { width: 40 }];

    (r.Mediciones || []).forEach((m, idx) => {
      const rowVals = esCuant
        ? [
            fmtFecha(m.FechaHora),
            m.Valor == null ? '-' : Number(m.Valor),
            m.ZScore == null ? '-' : Number(m.ZScore.toFixed(2)),
            ESTADO_TEXTO[m.Estado] || '-',
            m.Violaciones && m.Violaciones.length > 0 ? m.Violaciones.map((v) => v.Qcv_Regla).join(', ') : '-',
            m.UsuarioNombre || '-',
            m.Nota || '-',
          ]
        : [
            fmtFecha(m.FechaHora),
            m.ValorCualitativo || '-',
            m.Aprobado === true ? 'SI' : m.Aprobado === false ? 'NO' : '-',
            ESTADO_TEXTO[m.Estado] || '-',
            m.UsuarioNombre || '-',
            m.Nota || '-',
          ];
      const dataRow = ws.insertRow(tableStartRow + 1 + idx, rowVals);
      const estadoCol = esCuant ? 4 : 4;
      if (m.Estado === 3) dataRow.getCell(estadoCol).font = { color: { argb: 'FFF44336' }, bold: true };
      else if (m.Estado === 4) dataRow.getCell(estadoCol).font = { color: { argb: 'FFFF9800' }, bold: true };
      else if (m.Estado === 2) dataRow.getCell(estadoCol).font = { color: { argb: 'FF4CAF50' } };
    });
  }

  const stamp = `${fmtDate(header.fechaDesde).replace(/\//g, '')}_${fmtDate(header.fechaHasta).replace(/\//g, '')}`;
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName || `Informe_LeveyJennings_${stamp || 'westgard'}.xlsx`);
};
